import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDialog } from '../../context/DialogContext';
import { customAuth } from '../../services/customAuth';
import { SponsorService } from '../../services/sponsorService';
import PasswordResetModal from './PasswordResetModal';
import InvitationsPanel from '../InvitationsPanel';
import styles from './CustomLoginModal.module.css';

interface CustomLoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (user: any) => void;
  mode?: 'login' | 'signup';
  onCreateAccount?: () => void;
}

const CustomLoginModal: React.FC<CustomLoginModalProps> = ({ 
  isOpen, 
  onClose, 
  onSuccess, 
  mode = 'login',
  onCreateAccount 
}) => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [invitationToken, setInvitationToken] = useState<string | null>(null);
  const [invitationData, setInvitationData] = useState<any>(null);
  const [showInvitationConfirm, setShowInvitationConfirm] = useState(false);
  const [searchParams] = useSearchParams();
  
  const { showAlert } = useDialog();
  const navigate = useNavigate();

  // Check for invitation token on mount
  useEffect(() => {
    const token = searchParams.get('invitation');
    if (token && isOpen && mode === 'signup') {
      setInvitationToken(token);
      loadInvitationData(token);
    } else if (!token && isOpen) {
      // No invitation token - clear it
      setInvitationToken(null);
      setInvitationData(null);
    }
  }, [searchParams, isOpen, mode]);

  const loadInvitationData = async (token: string) => {
    try {
      const data = await SponsorService.getInvitationByToken(token);
      setInvitationData(data);
      setEmail(data.to_email);
      setIsSignUp(true);
    } catch (error) {
      console.error('Error loading invitation:', error);
      // Invalid or expired invitation - proceed with normal signup
      setInvitationToken(null);
    }
  };

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      // Clear password and error on open
      setPassword('');
      setConfirmPassword('');
      setError('');
      setSuccess('');
      setName('');
      setShowPassword(false);
      setShowConfirmPassword(false);
      
      // Set initial mode based on prop
      setIsSignUp(mode === 'signup');
      setIsForgotPassword(false);
      setShowPasswordReset(false);
      
      // Check if there's an invitation token in the URL
      const hasInvitation = searchParams.get('invitation');
      
      // Only pre-fill email if remember me was previously checked AND we're in login mode
      // For signup mode, email should always start blank (unless invitation)
      if (mode === 'login') {
        const rememberedEmail = localStorage.getItem('rememberedEmail');
        if (rememberedEmail) {
          setEmail(rememberedEmail);
          setRememberMe(true);
        } else {
          setEmail('');
          setRememberMe(false);
        }
      } else if (!hasInvitation) {
        // Signup mode without invitation - clear email
        // If there IS an invitation, don't clear - let the invitation hook handle it
        setEmail('');
        setRememberMe(false);
      }
    }
  }, [isOpen, mode, searchParams]);

  // Handle remember me checkbox change
  const handleRememberMeChange = (checked: boolean) => {
    setRememberMe(checked);
    if (checked && email) {
      // Save email to localStorage when checking remember me
      localStorage.setItem('rememberedEmail', email);
    } else if (!checked) {
      // Remove email from localStorage when unchecking
      localStorage.removeItem('rememberedEmail');
      setEmail('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      if (isForgotPassword) {
        const result = await customAuth.forgotPassword(email);
        if (result.success) {
          setSuccess(result.message);
          setIsForgotPassword(false);
          setShowPasswordReset(true);
        } else {
          setError(result.message);
        }
      } else if (isSignUp) {
        // Validate passwords match
        if (password !== confirmPassword) {
          setError('Passwords do not match');
          setIsLoading(false);
          return;
        }
        
        // Pass invitation token if present
        const result = await customAuth.signUp(email, password, name, invitationToken || undefined);
        if (result.success) {
          // If signed up via invitation and email was auto-verified, auto-login
          if (invitationToken && invitationData && result.emailVerified) {
            // Auto-login the user
            const loginResult = await customAuth.signIn(email, password);
            if (loginResult.success && loginResult.user) {
              onSuccess(loginResult.user);
              onClose();
              setShowInvitationConfirm(true);
            } else {
              // Login failed - shouldn't happen, but handle gracefully
              setError('Account created but login failed. Please try logging in manually.');
            }
          } else if (invitationToken && invitationData) {
            // Invitation signup but email not auto-verified (shouldn't happen)
            onClose();
            navigate(`/verify-email?email=${encodeURIComponent(email)}`);
          } else {
            // Normal signup - redirect to verification page
            onClose();
            navigate(`/verify-email?email=${encodeURIComponent(email)}`);
          }
        } else {
          setError(result.message);
        }
      } else {
        const result = await customAuth.signIn(email, password);
        if (result.success && result.user) {
          onSuccess(result.user);
          onClose();
        } else {
          setError(result.message);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Authentication failed');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2>
            {isForgotPassword ? 'Reset Password' : (isSignUp ? 'Create Account' : 'Sign In')}
          </h2>
          <button onClick={onClose} className={styles.closeButton}>×</button>
        </div>
        
        {invitationData && isSignUp && (
          <div className={styles.invitationBanner}>
            <strong>{invitationData.from_name}</strong> invited you to join Recovery Commons
            {invitationData.relationship_type !== 'friend' && (
              <> as their <strong>{invitationData.relationship_type}</strong></>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className={styles.form}>
          {isSignUp && (
            <div className={styles.formGroup}>
              <label htmlFor="name">First Name</label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Enter your first name"
              />
            </div>
          )}

          <div className={styles.formGroup}>
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              readOnly={!!invitationToken}
              disabled={!!invitationToken}
              className={invitationToken ? styles.readOnlyInput : ''}
            />
          </div>

          {!isForgotPassword && (
            <div className={styles.formGroup}>
              <label htmlFor="password">Password</label>
              <div className={styles.passwordInputContainer}>
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className={styles.passwordInput}
                />
                <button
                  type="button"
                  className={styles.togglePasswordButton}
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>
          )}

          {isSignUp && !isForgotPassword && (
            <div className={styles.formGroup}>
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className={styles.passwordInputContainer}>
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className={styles.passwordInput}
                />
                <button
                  type="button"
                  className={styles.togglePasswordButton}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>
          )}

          {!isSignUp && !isForgotPassword && (
            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => handleRememberMeChange(e.target.checked)}
                />
                Remember my email
              </label>
            </div>
          )}

          {error && <div className={styles.error}>{error}</div>}
          {success && <div className={styles.success}>{success}</div>}

          <button 
            type="submit" 
            className={styles.submitButton}
            disabled={isLoading}
          >
            {isLoading ? 'Processing...' : (
              isForgotPassword ? 'Send Reset Code' : (isSignUp ? 'Create Account' : 'Sign In')
            )}
          </button>
        </form>

        <div className={styles.switchMode}>
          {isForgotPassword ? (
            <div>
              <button 
                type="button" 
                onClick={() => setIsForgotPassword(false)}
                className={styles.switchButton}
              >
                Back to Sign In
              </button>
            </div>
          ) : (
            <div className={styles.switchModeContainer}>
              <button 
                type="button" 
                onClick={() => setIsSignUp(!isSignUp)}
                className={styles.switchButton}
              >
                {isSignUp ? 'Already have an account? Sign In' : 'Need an account? Create One'}
              </button>
              {!isSignUp && (
                <div className={styles.forgotPasswordSection}>
                  <button 
                    type="button" 
                    onClick={() => setIsForgotPassword(true)}
                    className={styles.forgotPasswordButton}
                  >
                    Forgot your password?
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      <PasswordResetModal
        isOpen={showPasswordReset}
        onClose={() => {
          setShowPasswordReset(false);
          onClose();
        }}
        email={email}
      />
      
      {/* Show invitation confirmation panel after invited user signs up */}
      {invitationData && (
        <InvitationsPanel
          isOpen={showInvitationConfirm}
          onClose={() => {
            setShowInvitationConfirm(false);
            // Navigate to verification or onboarding after confirming invitation
            navigate('/onboarding');
          }}
          onInvitationResponded={() => {
            setShowInvitationConfirm(false);
            navigate('/onboarding');
          }}
        />
      )}
    </div>
  );
};

export default CustomLoginModal;
